"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.measuresDocumentSchema = exports.scoringParametersSchema = exports.collectionTypeSchema = exports.measureNumberSchema = exports.STANDARD_SPEC_VERSION = exports.BENCHMARK_SOURCE_CMS_FILE = void 0;
exports.measureIdentity = measureIdentity;
exports.parseMeasuresDocument = parseMeasuresDocument;
// `config/measures/<year>.yaml` (SPEC §14.3, §5.2; CR-001 §10.1): the scoring parameters, pathways and the
// measure catalogue of one performance year. The year is the file stem (a copy renamed `2027.yaml` is a new
// year with no edit, J-017), so the document carries no year of its own. Every threshold is a value here,
// never a literal in scoring code (D-997, D-1015, D-1016).
const zod_1 = require("zod");
const config_issue_js_1 = require("./config-issue.js");
const report_rules_js_1 = require("./report-rules.js");
const scoring_js_1 = require("../scoring.js");
// The value lists of `ref.measure`'s CHECK constraints (packages/schema/src/db/aco-ref.ts).
const COLLECTION_TYPES = ["MIPS_CQM", "ECQM", "MEDICARE_CQM", "CLAIMS", "CAHPS"];
const AGGREGATION_RULES = ["ANY_MET", "MOST_RECENT"];
const QUALITY_MAX_METHODS = ["MEASURES_SCORED", "CONFIGURED"];
const SCORE_EST_METHODS = ["PROJECTION_PCT", "SUM_OF_CATEGORIES"];
/** `scoring_parameters.benchmark_source` value that selects the CMS rows instead of a named set. */
exports.BENCHMARK_SOURCE_CMS_FILE = "CMS_FILE";
/** `ref.measure.spec_version`'s default: the version a group scores unless it names another (D-1000). */
exports.STANDARD_SPEC_VERSION = "standard";
exports.measureNumberSchema = zod_1.z.string().regex(/^\d{3}$/, "measure number must be 3 digits");
exports.collectionTypeSchema = zod_1.z.enum(COLLECTION_TYPES);
const percentSchema = zod_1.z.number().min(0).max(100);
const nonNegativeSchema = zod_1.z.number().nonnegative();
exports.scoringParametersSchema = zod_1.z.strictObject({
    data_completeness_threshold: percentSchema,
    case_minimum: zod_1.z.int().nonnegative(),
    small_practice_points_low_completeness: nonNegativeSchema,
    small_practice_points_no_benchmark: nonNegativeSchema,
    topped_out_cap: nonNegativeSchema,
    partial_points_method: zod_1.z.enum(scoring_js_1.PARTIAL_POINTS_METHODS),
    aco_quality_performance_standard: percentSchema,
    patient_match_fields: zod_1.z
        .string()
        .regex(/^[a-z_]+(,[a-z_]+)*$/, "patient_match_fields is a comma-separated list of field names"),
    performance_rate_decimals: zod_1.z.int().nonnegative(),
    completeness_basis: zod_1.z.enum(scoring_js_1.COMPLETENESS_BASES),
    scoring_ruleset: zod_1.z.enum(scoring_js_1.SCORING_RULESETS),
    case_minimum_basis: zod_1.z.enum(scoring_js_1.CASE_MINIMUM_BASES),
    quality_max_method: zod_1.z.enum(QUALITY_MAX_METHODS),
    score_est_method: zod_1.z.enum(SCORE_EST_METHODS),
    benchmark_source: zod_1.z.string().min(1),
    default_source_system: zod_1.z
        .string()
        .regex(/^[A-Z][A-Z0-9_]*$/, "default_source_system is an upper-case code such as MODERN_PRISM"),
});
const pathwaySchema = zod_1.z.strictObject({
    code: zod_1.z.string().min(1),
    type: zod_1.z.string().min(1),
    display_name: zod_1.z.string().min(1),
    quality_points_max: nonNegativeSchema,
    measures: zod_1.z.array(exports.measureNumberSchema).min(1),
});
const stratumSchema = zod_1.z.strictObject({
    code: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    is_inverse: zod_1.z.boolean().default(false),
    display_order: zod_1.z.int().nonnegative(),
});
const PLACEHOLDER = /\{([^{}]*)\}/g;
/** Text a rule shows, `{reason}` its only placeholder (CR-002 §6). */
const ruleTextSchema = zod_1.z
    .string()
    .min(1, "label is empty")
    .superRefine((value, ctx) => {
    for (const [placeholder, name = ""] of value.matchAll(PLACEHOLDER)) {
        if (report_rules_js_1.REPORT_RULE_PLACEHOLDERS.includes(name))
            continue;
        const message = `placeholder ${placeholder} is not one of ${report_rules_js_1.REPORT_RULE_PLACEHOLDERS.map((p) => `{${p}}`).join(", ")}`;
        ctx.addIssue({ code: "custom", message });
    }
});
const reportRuleSchema = zod_1.z.strictObject({
    label: ruleTextSchema,
    consolidated_label: ruleTextSchema,
    action: ruleTextSchema,
    gap: zod_1.z.boolean(),
});
// A quality data code, as §6.6 lists them: 4–7 letters and digits (G9717, M1212, 3017F).
const qualityCodeSchema = zod_1.z
    .string()
    .regex(/^[A-Z0-9]{4,7}$/, "code is 4-7 capital letters and digits");
// CR-002 CR2-111: the report wording of one measure; which keys it needs follows its number.
const reportSchema = zod_1.z.strictObject({
    rules: zod_1.z.record(zod_1.z.string(), reportRuleSchema),
    exclusion_labels: zod_1.z.record(qualityCodeSchema, zod_1.z.string().min(1, "label is empty")).default({}),
});
const measureSchema = zod_1.z.strictObject({
    number: exports.measureNumberSchema,
    display_number: zod_1.z.string().min(1),
    // CR-002 CR2-111: DPS, DM, HTN, BCS, CRS; Screen 1's headers and Screen 12's tabs use it.
    short_label: zod_1.z.string().min(1),
    title: zod_1.z.string().min(1),
    collection_type: exports.collectionTypeSchema,
    spec_version: zod_1.z.string().min(1).default(exports.STANDARD_SPEC_VERSION),
    is_inverse: zod_1.z.boolean().default(false),
    is_outcome: zod_1.z.boolean().default(false),
    aggregation_rule: zod_1.z.enum(AGGREGATION_RULES),
    display_order: zod_1.z.int().nonnegative(),
    aliases: zod_1.z.array(zod_1.z.string().min(1)).default([]),
    strata: zod_1.z.array(stratumSchema).default([]),
    report: reportSchema.optional(),
});
/** `ref.measure`'s natural key within a year: number, collection type and spec version. */
function measureIdentity(measure) {
    return `${measure.number} (${measure.collection_type}, ${measure.spec_version})`;
}
function checkMeasureEntries(measures, ctx) {
    const identities = new Map();
    const aliases = new Map();
    measures.forEach((measure, index) => {
        const identity = measureIdentity(measure);
        const first = identities.get(identity);
        if (first === undefined)
            identities.set(identity, index);
        else {
            ctx.addIssue({
                code: "custom",
                path: ["measures", index, "number"],
                message: `measure ${identity} is already defined by measures[${first}]`,
            });
        }
        measure.aliases.forEach((alias, a) => {
            const owner = aliases.get(alias);
            if (owner === undefined)
                aliases.set(alias, index);
            else {
                ctx.addIssue({
                    code: "custom",
                    path: ["measures", index, "aliases", a],
                    message: `alias ${alias} is already used by measures[${owner}]`,
                });
            }
        });
    });
}
function checkStrata(measures, pathways, ctx) {
    const inPathway = new Set(pathways.flatMap((pathway) => pathway.measures));
    measures.forEach((measure, index) => {
        if (measure.strata.length > 0 && !inPathway.has(measure.number)) {
            ctx.addIssue({
                code: "custom",
                path: ["measures", index, "number"],
                message: `measure ${measure.number} has strata but no pathway includes it`,
            });
        }
        const codes = new Set();
        measure.strata.forEach((stratum, s) => {
            if (codes.has(stratum.code)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["measures", index, "strata", s, "code"],
                    message: `stratum ${stratum.code} is already defined for measure ${measure.number}`,
                });
            }
            codes.add(stratum.code);
        });
    });
}
function checkPathways(pathways, measures, ctx) {
    const numbers = new Set(measures.map((measure) => measure.number));
    const codes = new Map();
    pathways.forEach((pathway, index) => {
        const first = codes.get(pathway.code);
        if (first === undefined)
            codes.set(pathway.code, index);
        else {
            ctx.addIssue({
                code: "custom",
                path: ["pathways", index, "code"],
                message: `pathway ${pathway.code} is already defined by pathways[${first}]`,
            });
        }
        pathway.measures.forEach((number, m) => {
            if (!numbers.has(number)) {
                ctx.addIssue({
                    code: "custom",
                    path: ["pathways", index, "measures", m],
                    message: `measure ${number} is not in measures`,
                });
            }
        });
    });
}
/**
 * CR-002 CR2-111, CR2-113: each measure with a report table (§6) carries one `report` block, on one of
 * its entries, holding exactly its rule keys; a measure without one carries none.
 */
function checkReports(measures, ctx) {
    const reported = new Map();
    measures.forEach((measure, index) => {
        const report = measure.report;
        if (report === undefined)
            return;
        const keys = report_rules_js_1.REPORT_RULE_KEYS[measure.number];
        const at = ["measures", index, "report"];
        if (keys === undefined) {
            const message = `measure ${measure.number} has no report table (CR-002 §6); remove report`;
            ctx.addIssue({ code: "custom", path: at, message });
            return;
        }
        const first = reported.get(measure.number);
        if (first !== undefined) {
            const message = `the report of measure ${measure.number} is already given by measures[${first}]`;
            ctx.addIssue({ code: "custom", path: at, message });
        }
        else
            reported.set(measure.number, index);
        for (const key of keys) {
            if (!Object.hasOwn(report.rules, key)) {
                const message = `rule key ${key} is missing for measure ${measure.number}`;
                ctx.addIssue({ code: "custom", path: [...at, "rules"], message });
            }
        }
        for (const key of Object.keys(report.rules)) {
            if (!keys.includes(key)) {
                const message = `rule key ${key} is not a rule of measure ${measure.number}; its keys: ${keys.join(", ")}`;
                ctx.addIssue({ code: "custom", path: [...at, "rules", key], message });
            }
        }
    });
    const numbers = new Set(measures.map((measure) => measure.number));
    for (const number of Object.keys(report_rules_js_1.REPORT_RULE_KEYS)) {
        if (numbers.has(number) && !reported.has(number)) {
            const message = `measure ${number} needs a report block (CR-002 §6) on one of its entries`;
            ctx.addIssue({ code: "custom", path: ["measures"], message });
        }
    }
}
exports.measuresDocumentSchema = zod_1.z
    .strictObject({
    scoring_parameters: exports.scoringParametersSchema,
    pathways: zod_1.z.array(pathwaySchema).min(1),
    measures: zod_1.z.array(measureSchema).min(1),
})
    .superRefine((doc, ctx) => {
    checkMeasureEntries(doc.measures, ctx);
    checkStrata(doc.measures, doc.pathways, ctx);
    checkPathways(doc.pathways, doc.measures, ctx);
    checkReports(doc.measures, ctx);
});
/** Parses `config/measures/<year>.yaml`; issues name the document `measures/<year>`. */
function parseMeasuresDocument(year, input) {
    return (0, config_issue_js_1.parseConfigDocument)(exports.measuresDocumentSchema, `measures/${year}`, input);
}
//# sourceMappingURL=measures.js.map